/**
 * EduOS API Service — Backend binding for YARUKSAI EduOS v5.0
 * Connects to: M7 (Coach), M8 (Analysis), M9 (Truth), M10 (Tasks + Gamification)
 */

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface ApiResponse<T = any> {
  data?: T;
  error?: string;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    ...options,
  });
  if (!res.ok) {
    const err = await res.text().catch(() => 'Unknown error');
    throw new Error(err);
  }
  return res.json();
}

// ============ M10: TASK ENGINE ============

export interface Task {
  task_id: string;
  type: string;
  text: string;
  title?: string;
  goal: string;
  difficulty: string;
  time_limit_minutes: number;
  evidence_required: string[];
  psychological_purpose: string;
  xp_reward: number;
  status: string;
  reasoning?: string;
  created_at?: string;
  expires_at?: string;
}

export interface RiskCheckResult {
  risk_level: 'none' | 'low' | 'medium' | 'high';
  action: string;
  message?: string;
  notify_parent?: boolean;
  disable_tasks?: boolean;
}

export interface TaskReasoning {
  task_id: string;
  explanation: string;
  technical_audit?: Record<string, any>;
}

export interface Gamification {
  total_xp: number;
  level: number;
  level_name: string;
  level_description: string;
  current_streak: number;
  max_streak: number;
  xp_in_level: number;
  xp_to_next: number;
  progress_pct: number;
  truth_bonus_xp: number;
  behavior_bonus_xp: number;
  last_activity_at?: string;
}

export interface BehaviorSummary {
  total_tasks: number;
  completed: number;
  completion_rate: number;
  avg_confidence: number;
  current_streak: number;
  max_streak: number;
  truth_trend: string;
}

export interface Dashboard {
  student_id: string;
  active_tasks: Task[];
  total_active: number;
  gamification: Gamification;
  behavior: BehaviorSummary;
  task_counts: {
    total: number;
    completed: number;
    failed: number;
    active: number;
    expired: number;
  };
  mood_message: string;
  dashboard_version: string;
}

export interface TaskGenerateResult {
  student_id: string;
  daily_plan: {
    tasks: Task[];
    total_tasks: number;
    difficulty: string;
    strategy: string;
  };
  truth_context: {
    truth_score: number;
    trust_level: string;
    dominant_issue: string;
  };
  gamification: Gamification;
}

export interface SubmitResult {
  task_id: string;
  validation: {
    completed: boolean;
    confidence: number;
    xp_earned: number;
    issues: string[];
  };
  feedback: {
    truth_delta: number;
    streak: number;
    max_streak: number;
    feedback_message: string;
    indicators: string[];
  };
  task_status: string;
  gamification: Gamification;
}

export interface TaskHistory {
  student_id: string;
  tasks: Task[];
  total: number;
  behavior_summary: BehaviorSummary;
  gamification: Gamification;
}

// Dashboard
export const getDashboard = (studentId: string) =>
  request<Dashboard>(`/tasks/dashboard/${studentId}`);

// Generate daily tasks
export const generateTasks = (studentId: string, truthData: any, subject: string) =>
  request<TaskGenerateResult>('/tasks/generate', {
    method: 'POST',
    body: JSON.stringify({
      student_id: studentId,
      truth_data: truthData,
      subject,
    }),
  });

// Submit task evidence
export const submitTask = (studentId: string, taskId: string, evidence: Record<string, any>) =>
  request<SubmitResult>('/tasks/submit', {
    method: 'POST',
    body: JSON.stringify({
      student_id: studentId,
      task_id: taskId,
      evidence,
    }),
  });

// Active tasks
export const getActiveTasks = (studentId: string) =>
  request<{ active_tasks: Task[]; gamification: Gamification }>(`/tasks/active/${studentId}`);

// Task history
export const getTaskHistory = (studentId: string, limit = 20) =>
  request<TaskHistory>(`/tasks/history/${studentId}?limit=${limit}`);

// Stats
export const getStats = () => request<any>('/tasks/stats');

// ============ M9: TRUTH ENGINE ============

export const quickTruthCheck = (studentId: string, claim: string, latency: number) =>
  request<any>('/truth/quick-check', {
    method: 'POST',
    body: JSON.stringify({ student_id: studentId, claim, latency }),
  });

// ============ M7/M8: COACH ============

export const sendCoachMessage = (studentId: string, message: string, responseTimeMs: number) =>
  request<any>('/coach/message', {
    method: 'POST',
    body: JSON.stringify({
      student_id: studentId,
      message,
      response_time_ms: responseTimeMs,
    }),
  });

// ============ M8: COACH PROFILE (49 Alan) ============

export const getCoachProfile = (studentId: string) =>
  request<Record<string, any>>(`/coach/profile/${studentId}`);

// ============ M9: TRUTH ENGINE ============

export const getTruthScore = (studentId: string) =>
  request<{ quick_truth_score: number; trust_level: string; consistency: number; latency: number; behavior: number; mode: string }>('/truth/quick-check', {
    method: 'POST',
    body: JSON.stringify({ student_id: studentId, claim: '_truth_check_', latency: 500 }),
  });

// ============ M13: EXPLAINABILITY + RISK ============

export const getTaskReasoning = (taskId: string) =>
  request<TaskReasoning>(`/tasks/reasoning/${taskId}`);

export const checkRisk = (studentId: string, text: string) =>
  request<RiskCheckResult>('/tasks/risk-check', {
    method: 'POST',
    body: JSON.stringify({ student_id: studentId, text }),
  });

// Health check
export const healthCheck = () => request<any>('/health');

// ============ M11: REPORT ENGINE ============

export interface ReportSummary {
  report_id: string;
  report_type: string;
  title: string;
  status: string;
  created_at: string;
  version: string;
}

export const generateReport = (studentId: string, reportType: string,
  studentProfile: any, conversationHistory?: any[], truthData?: any, taskHistory?: any[]) =>
  request<any>('/reports/generate', {
    method: 'POST',
    body: JSON.stringify({
      student_id: studentId,
      report_type: reportType,
      student_profile: studentProfile,
      conversation_history: conversationHistory || [],
      truth_data: truthData || {},
      task_history: taskHistory || [],
    }),
  });

export const getReport = (reportId: string) =>
  request<any>(`/reports/${reportId}`);

export const getStudentReports = (studentId: string) =>
  request<{ reports: ReportSummary[] }>(`/reports/student/${studentId}`);

// ============ M14: ADAPTIVE PLANNER ============

export interface StudyPlan {
  plan_id: string;
  plan_type: string;
  period_start: string;
  period_end: string;
  status: string;
  plan_data: any;
  version: number;
}

export const generatePlan = (studentId: string, planType: string, studentData?: any) =>
  request<{ plan: any }>('/plans/generate', {
    method: 'POST',
    body: JSON.stringify({
      student_id: studentId,
      plan_type: planType,
      student_data: studentData || {},
    }),
  });

export const getPlans = (studentId: string) =>
  request<{ plans: Record<string, StudyPlan> }>(`/plans/${studentId}`);

export const getPlan = (studentId: string, planType: string) =>
  request<{ plan: StudyPlan }>(`/plans/${studentId}/${planType}`);

export const adjustPlan = (studentId: string, planType: string,
  adjustedBy: string, adjustment: any) =>
  request<any>('/plans/adjust', {
    method: 'POST',
    body: JSON.stringify({
      student_id: studentId,
      plan_type: planType,
      adjusted_by: adjustedBy,
      adjustment,
    }),
  });

export const submitPlanFeedback = (studentId: string, planType: string,
  source: string, feedback: any) =>
  request<any>('/plans/feedback', {
    method: 'POST',
    body: JSON.stringify({
      student_id: studentId,
      plan_type: planType,
      source,
      feedback,
    }),
  });

// ============ M15: TEACHER PANEL ============

export const teacherLogin = (authCode: string) =>
  request<any>('/teacher/login', {
    method: 'POST',
    body: JSON.stringify({ auth_code: authCode }),
  });

export const teacherRegister = (name: string, email?: string,
  school?: string, subject?: string) =>
  request<any>('/teacher/register', {
    method: 'POST',
    body: JSON.stringify({ name, email, school, subject }),
  });

export const getTeacherStudents = (teacherId: string) =>
  request<{ students: any[] }>(`/teacher/${teacherId}/students`);

export const getTeacherStudentOverview = (teacherId: string, studentId: string) =>
  request<any>(`/teacher/${teacherId}/student/${studentId}`);

export const addTeacherNote = (teacherId: string, studentId: string,
  noteType: string, subject: string, content: string, priority?: string) =>
  request<any>('/teacher/note', {
    method: 'POST',
    body: JSON.stringify({
      teacher_id: teacherId,
      student_id: studentId,
      note_type: noteType,
      subject,
      content,
      priority: priority || 'normal',
    }),
  });

// ============ M15: PARENT PANEL ============

export const parentLogin = (authCode: string) =>
  request<any>('/parent/login', {
    method: 'POST',
    body: JSON.stringify({ auth_code: authCode }),
  });

export const parentRegister = (name: string, email?: string,
  phone?: string, relation?: string) =>
  request<any>('/parent/register', {
    method: 'POST',
    body: JSON.stringify({ name, email, phone, relation }),
  });

export const getParentDaily = (parentId: string, studentId: string) =>
  request<any>(`/parent/${parentId}/daily/${studentId}`);

export const addParentObservation = (parentId: string, studentId: string,
  observation: any) =>
  request<any>('/parent/observation', {
    method: 'POST',
    body: JSON.stringify({
      parent_id: parentId,
      student_id: studentId,
      observation,
    }),
  });

export const getParentAlerts = (parentId: string, studentId: string) =>
  request<{ alerts: any[] }>(`/parent/${parentId}/alerts/${studentId}`);

export const getParentNotifications = (parentId: string) =>
  request<{ notifications: any[] }>(`/parent/${parentId}/notifications`);

// ============ M16: FEEDBACK ENGINE ============

export const submitFeedbackEvent = (studentId: string, source: string,
  eventType: string, data: any) =>
  request<any>('/feedback/event', {
    method: 'POST',
    body: JSON.stringify({ student_id: studentId, source, event_type: eventType, data }),
  });

export const recordTaskCompleted = (studentId: string, taskData: any) =>
  request<any>('/feedback/task-completed', {
    method: 'POST',
    body: JSON.stringify({ student_id: studentId, task_data: taskData }),
  });

export const runFeedbackCycle = (studentId: string) =>
  request<any>(`/feedback/cycle/${studentId}`, { method: 'POST' });

export const runWeeklyCycle = (studentId: string) =>
  request<any>(`/feedback/weekly-cycle/${studentId}`, { method: 'POST' });

export const applyReportToPlan = (studentId: string, reportData: any) =>
  request<any>(`/feedback/apply-report/${studentId}`, {
    method: 'POST',
    body: JSON.stringify({ report_data: reportData }),
  });

export const getFeedbackStatus = (studentId: string) =>
  request<any>(`/feedback/status/${studentId}`);

export const getFeedbackMetrics = (studentId: string) =>
  request<any>(`/feedback/metrics/${studentId}`);

// ============ M17: ONBOARDING ============

export const startOnboarding = (studentId: string) =>
  request<any>(`/onboarding/start/${studentId}`, { method: 'POST' });

export const submitAssessment = (studentId: string, answers: any) =>
  request<any>(`/onboarding/assess/${studentId}`, {
    method: 'POST',
    body: JSON.stringify({ answers }),
  });

export const getFirstTask = (studentId: string) =>
  request<any>(`/onboarding/first-task/${studentId}`, { method: 'POST' });

export const getOnboardingStatus = (studentId: string) =>
  request<any>(`/onboarding/status/${studentId}`);

// ============ M18: RETENTION ============

export const logRetentionActivity = (studentId: string, tasksDone?: number, minutes?: number) =>
  request<any>(`/retention/activity/${studentId}`, {
    method: 'POST',
    body: JSON.stringify({ tasks_done: tasksDone || 0, minutes: minutes || 0 }),
  });

export const getRetentionStatus = (studentId: string) =>
  request<any>(`/retention/status/${studentId}`);

export const getRetentionMessages = (studentId: string) =>
  request<{ messages: any[] }>(`/retention/messages/${studentId}`);

export const markMessageRead = (messageId: string) =>
  request<any>(`/retention/message/${messageId}/read`, { method: 'POST' });

// ============ M19: METRICS ============

export const getMetricsDashboard = () =>
  request<any>('/metrics/dashboard');

export const computeMetrics = () =>
  request<any>('/metrics/compute', { method: 'POST' });

export const getStudentMetrics = (studentId: string) =>
  request<any>(`/metrics/student/${studentId}`);

// ============ AUDIT ============

export const getAuditTrail = (studentId: string) =>
  request<any>(`/audit/${studentId}`);

// ============ SYSTEM ============

export const getSystemModules = () =>
  request<any>('/system/modules');

export const getSystemStatus = () =>
  request<any>('/system/status');
